// Merges the badges a fan holds with the full catalogue, so the Badges strip
// can show what is still out there. Earned badges lead, newest first; the rest
// follow in catalogue order and render muted.

const keyOf = (badge) => String(badge?.key || badge?.badgeKey || "");

/**
 * @param {Array} earned   badges the fan holds (engagement /me `badges`)
 * @param {Array} catalog  every badge that exists (engagement /me `badgeCatalog`)
 * @returns {Array} [{ ...badge, earned: boolean }]
 */
export function mergeBadges(earned, catalog) {
  const held = Array.isArray(earned) ? earned : [];
  const all = Array.isArray(catalog) ? catalog : [];

  const mine = [...held]
    .sort((a, b) => new Date(b.earnedAt || 0) - new Date(a.earnedAt || 0))
    .map((b) => ({ ...b, earned: true }));

  // An artist-granted badge can be held more than once (one per artist), so
  // match on key rather than assuming the catalogue covers every held badge.
  const heldKeys = new Set(mine.map(keyOf).filter(Boolean));
  const rest = all.filter((b) => !heldKeys.has(keyOf(b))).map((b) => ({ ...b, earned: false }));

  return [...mine, ...rest];
}

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

// Where a badge's progress is read from, by the `metric` the catalogue names.
// All three sources are already in the engagement /me payload, so opening a
// badge costs no extra request.
function currentFor(metric, me) {
  if (!metric || !me) return null;
  if (metric === "fanPoints") return num(me.fanPoints);
  if (metric.startsWith("streak.")) {
    const streak = (me.streaks || {})[metric.slice("streak.".length)];
    return streak ? num(streak.current) : 0;
  }
  if (metric.startsWith("mission.")) {
    const key = metric.slice("mission.".length);
    const mission = (me.missions || []).find((m) => m.key === key);
    return mission ? num(mission.progress) : 0;
  }
  return null;
}

/**
 * What a fan must do to earn a badge, and how far along they are.
 *
 * @param {Object} badge  a merged badge (see mergeBadges)
 * @param {Object} me     the engagement /me payload
 * @returns {{ howTo: string, points: number|null, current: number|null,
 *            target: number|null, pct: number|null, remaining: number|null,
 *            earned: boolean, earnedAt: string|null }}
 */
export function badgeProgress(badge, me) {
  const b = badge || {};
  const earned = !!b.earned;
  const target = Number.isFinite(Number(b.target)) && Number(b.target) > 0 ? Number(b.target) : null;
  // An older backend sends no `howTo`; the description still says what it takes.
  const howTo = b.howTo || b.description || "Keep listening and supporting artists to unlock this one.";

  const points = (b.points ?? null) === null ? null : num(b.points);
  const raw = earned ? target : currentFor(b.metric, me);
  const current = raw === null ? null : Math.min(num(raw), target || num(raw));
  let pct = target && current !== null ? Math.max(0, Math.min(100, Math.round((current / target) * 100))) : null;
  const remaining = target && current !== null ? Math.max(0, target - current) : null;

  // The Next badge card works out its own pct and one-line "what's left" from
  // the ladder, and it has them even when the backend sends no catalogue. Fall
  // back to those so opening that card still shows progress.
  let note = null;
  if (pct === null && Number.isFinite(Number(b.pct))) {
    pct = Math.max(0, Math.min(100, Math.round(Number(b.pct))));
    note = b.remainingText || null;
  }

  return { howTo, points, current, target, pct, remaining, note, earned, earnedAt: b.earnedAt || null };
}

/** A level badge is earned with fan points, so "what do I do" is the points table. */
export function isLevelBadge(badge) {
  const b = badge || {};
  return b.kind === "level" || b.metric === "fanPoints" || String(b.key || "").startsWith("level.");
}

/**
 * The handful of actions worth showing for a level badge: best paying first,
 * from the `pointActions` list the backend derives from the scoring rules.
 * Returns [] when the backend has not sent one yet.
 */
export function earningActions(me, limit = 6) {
  const actions = Array.isArray(me?.pointActions) ? me.pointActions : [];
  return actions.slice(0, limit).map((a) => ({
    key: a.key,
    label: a.label,
    // "10 pts + $1 each" reads better than repeating the formula.
    detail: a.perDollar ? `+${num(a.points)} & +1 per $` : `+${num(a.points)}`,
  }));
}

export default mergeBadges;
